import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { ContactLink } from "@/components/ContactDialog";
import { TopLogoBar } from "@/components/AppShell";
import { PasswordInput } from "@/components/PasswordInput";

export default function Auth() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("register"); // register | login | forgot
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const dest = location.state?.from || (localStorage.getItem("a2g_result") ? "/paywall" : "/oggi");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        setForgotSent(true);
        setLoading(false);
        return;
      }
      const u = mode === "register"
        ? await register(form.email, form.password, form.name, form.phone)
        : await login(form.email, form.password);
      toast.success(mode === "register" ? "Account creato, ti diamo il benvenuto!" : "Ciao, che bello rivederti!");
      const target = u?.subscription_active ? (u?.profile_id ? "/oggi" : "/test") : dest;
      navigate(target, { replace: true });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  const inputCls = "mt-1 w-full rounded-2xl border border-[#27272A] bg-[#121215] px-4 py-3.5 text-[#F4F4F5] outline-none placeholder:text-[#8A8E96] focus:border-[#D4AF37]/50";

  const copy = {
    register: { eyebrow: "Crea il tuo account", title: "Salva i tuoi progressi", sub: "Un account ti serve per iniziare il percorso e ritrovare le tue riflessioni." },
    login: { eyebrow: "Ciao di nuovo", title: "Accedi al tuo percorso", sub: "Inserisci le tue credenziali per continuare." },
    forgot: { eyebrow: "Recupero accesso", title: "Password dimenticata?", sub: "Inserisci la tua email: ti invieremo un link per reimpostare la password." },
  }[mode];

  return (
    <div className="min-h-screen px-5 pb-10 pt-6" data-testid="auth-page">
      <TopLogoBar onBack={() => navigate(-1)} testid="auth-back-button" />

      <div className="mt-10">
        <p className="eyebrow">{copy.eyebrow}</p>
        <h1 className="font-serif-el mt-2 text-3xl font-semibold text-[#FAF9F6]">{copy.title}</h1>
        <p className="mt-2 text-base text-[#B4B4BC]">{copy.sub}</p>
      </div>

      {mode === "forgot" && forgotSent ? (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-10 rounded-3xl border border-[#D4AF37]/25 bg-[#121215] p-6 text-center" data-testid="forgot-sent">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#D4AF37]/12 text-[#D4AF37]">
            <MailCheck size={26} />
          </div>
          <p className="mt-4 text-xl text-[#FAF9F6] font-semibold">Controlla la tua email</p>
          <p className="mt-2 text-base text-[#B4B4BC]">Se esiste un account con <b className="text-[#D4D4D8]">{form.email}</b>, riceverai un link per reimpostare la password. Il link scade tra un'ora.</p>
          <button onClick={() => { setMode("login"); setForgotSent(false); }} data-testid="forgot-back-login" className="btn-outline-gold mt-6 w-full rounded-full px-6 py-3.5 text-base">
            Torna all'accesso
          </button>
        </motion.div>
      ) : (
        <motion.form onSubmit={submit} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-8 space-y-3.5">
          {mode === "register" && (
            <div>
              <label className="text-sm font-semibold text-[#B4B4BC]">Nome</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="auth-name-input" placeholder="Il tuo nome" className={inputCls} />
            </div>
          )}
          {mode === "register" && (
            <div>
              <label className="text-sm font-semibold text-[#B4B4BC]">Telefono</label>
              <input type="tel" required pattern="\+?[0-9 ]{6,20}" title="Solo numeri, eventualmente con + iniziale" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="auth-phone-input" placeholder="+39 333 1234567" autoComplete="tel" className={inputCls} />
            </div>
          )}
          <div>
            <label className="text-sm font-semibold text-[#B4B4BC]">Email</label>
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="auth-email-input" placeholder="nome@email.it" className={inputCls} />
          </div>
          {mode !== "forgot" && (
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold text-[#B4B4BC]">Password</label>
                {mode === "login" && (
                  <button type="button" onClick={() => setMode("forgot")} data-testid="auth-forgot-link" className="text-sm font-semibold text-[#D4AF37]">Password dimenticata?</button>
                )}
              </div>
              <PasswordInput value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} testId="auth-password-input" autoComplete={mode === "register" ? "new-password" : "current-password"} className={inputCls} />
              {mode === "login" && (
                <button type="button" onClick={() => setMode("forgot")} data-testid="auth-forgot-link-bottom" className="mt-2 text-sm font-semibold text-[#E5C158] underline underline-offset-4">
                  Non ricordi la password? Recuperala qui
                </button>
              )}
            </div>
          )}
          <button type="submit" disabled={loading} data-testid="auth-submit-button" className="btn-gold w-full rounded-full px-6 py-4 text-base uppercase tracking-wide">
            {loading ? "Un momento…" : mode === "register" ? "Crea account" : mode === "forgot" ? "Invia link di reset" : "Accedi"}
          </button>
        </motion.form>
      )}

      {mode === "forgot" && !forgotSent ? (
        <p className="mt-6 text-center text-base text-[#B4B4BC]">
          Ti sei ricordato?{" "}
          <button onClick={() => setMode("login")} data-testid="auth-back-to-login" className="font-semibold text-[#D4AF37]">Accedi</button>
        </p>
      ) : mode !== "forgot" ? (
        <p className="mt-6 text-center text-base text-[#B4B4BC]">
          {mode === "register" ? "Hai già un account?" : "Non hai un account?"}{" "}
          <button onClick={() => setMode(mode === "register" ? "login" : "register")} data-testid="auth-toggle-button" className="font-semibold text-[#D4AF37]">
            {mode === "register" ? "Accedi" : "Registrati"}
          </button>
        </p>
      ) : null}
      <p className="mt-3 text-center text-sm text-[#8A8E96]"><Link to="/" className="underline">Torna alla home</Link></p>
      <div className="mt-6 flex justify-center"><ContactLink testId="auth-contact-link" /></div>
    </div>
  );
}
